// Confirm a new sign-in address, from the link sent to it
// (PLAN-2026-10-28-by-email §3.5, scenario A6, B-548).
//
// `/email-change#<token>`, the token in the fragment as a reset's is, taken
// out of the address bar once read. ONE PRESS, never on opening: a mail
// scanner that opens every link would otherwise move an account to an inbox
// nobody chose — a mistyped address that happens to be somebody else's. The
// link carries a token and not the address, so which address it was is said
// after the press; and signed in or not, it works.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../data/session';
import { useTier } from '../shell/useTier';
import { Wizard, type WizardStep } from '../templates/Wizard';
import { Text } from '../kit/ui';

/** A token as `newToken` makes them: base64url, 43 characters for 32 bytes. */
const TOKEN = /^[A-Za-z0-9_-]{20,}$/;

type Stage = 'press' | 'done' | 'failed';

export function EmailChangeScreen() {
  const { confirmEmailChange } = useSession();
  const tier = useTier();
  const navigate = useNavigate();
  const location = useLocation();
  const [token] = useState<string | null>(() => {
    const raw = location.hash.replace(/^#/, '');
    return TOKEN.test(raw) ? raw : null;
  });
  const [stage, setStage] = useState<Stage>(token === null ? 'failed' : 'press');
  const [to, setTo] = useState('');
  const [problem, setProblem] = useState<string | null>(
    token === null ? 'This page opens from the link in a change-of-email message.' : null,
  );
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (location.hash !== '') navigate(location.pathname, { replace: true });
  }, [location.hash, location.pathname, navigate]);

  const press = async () => {
    if (pending || token === null) return;
    setPending(true);
    const outcome = await confirmEmailChange(token);
    setPending(false);
    if (outcome.ok) {
      setTo(outcome.to);
      setStage('done');
      return;
    }
    setProblem(outcome.message);
    if (
      outcome.code === 'unauthorized' ||
      outcome.code === 'conflict' ||
      outcome.code === 'invalid'
    )
      setStage('failed');
  };

  const steps: Record<Stage, WizardStep> = {
    press: {
      id: 'press',
      title: 'Change your email?',
      hint: 'Your account will sign in with this address from now on. Your leagues, history and trophies stay as they are.',
      content: (
        <Text tone="secondary" size="sm" block>
          Your old address gets a note saying so, with a way to undo it for 24 hours.
        </Text>
      ),
      error: problem,
      doneLabel: pending ? 'Changing…' : 'Change my email',
    },
    done: {
      id: 'done',
      title: 'Email changed',
      hint: `Your account signs in with ${to} now.`,
      content: null,
      doneLabel: 'Done',
    },
    failed: {
      id: 'failed',
      title: 'That link will not work',
      hint: problem ?? 'That link did not work.',
      content: (
        <Text tone="secondary" size="sm" block>
          Ask again from Account → Sign-in, and use the newest link.
        </Text>
      ),
      doneLabel: 'Done',
    },
  };

  return (
    <div className="fsp-frame" data-tier={tier}>
      <Wizard
        title="Your email"
        steps={[steps[stage]]}
        index={0}
        onIndex={() => undefined}
        onDone={() => {
          if (stage === 'press') void press();
          else navigate('/', { replace: true });
        }}
        onCancel={stage === 'press' ? () => navigate('/', { replace: true }) : undefined}
      />
    </div>
  );
}
